import React, { useEffect, useCallback, useRef, useState, useMemo } from "react";
import {
  Box,
  Divider,
  Typography,
  Tab,
  Tabs,
  Paper,
  Switch,
  FormControlLabel,
} from "@mui/material";

import { WorkflowRunDetails } from "./WorkflowTypes";
import GeneralTabPanel from "../AgentDetail/GeneralTabPanel.jsx";
import { marked } from "marked";
import ReactFlow, {
  Controls,
  Background,
  Panel,
  Node,
  Edge,
  NodeTypes,
  useReactFlow,
  ReactFlowProvider,
  useNodesState,
} from "reactflow";

interface WorkflowDetailsContentProps {
  workflowRunDetails: WorkflowRunDetails;
  activeTabIndex: number;
  onClose: () => void;
  onTabChange: (event: React.SyntheticEvent, newValue: number) => void;
  nodes: Node[];
  edges: Edge[];
  nodeTypes?: NodeTypes;
  layoutKey?: string;
}

const RunVisualization: React.FC<{
  nodes: Node[];
  edges: Edge[];
  nodeTypes?: NodeTypes;
  layoutKey?: string;
}> = ({ nodes: propNodes, edges: allEdges, nodeTypes, layoutKey }) => {
  const reactFlowInstance = useReactFlow();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const positionsRef = useRef<Record<string, { x: number; y: number }>>({});
  const initializedRef = useRef(false);

  // Toggle for data mapping edges — persisted in localStorage under the same layout key
  const [showDataMappings, setShowDataMappings] = useState(() => {
    if (!layoutKey) return false;
    try {
      const saved = localStorage.getItem(layoutKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed._showDataMappings === true;
      }
    } catch { /* ignore */ }
    return false;
  });

  // Filter edges based on toggle
  const displayedEdges = useMemo(
    () => showDataMappings ? allEdges : allEdges.filter(e => !e.data?.isDataFlow),
    [allEdges, showDataMappings]
  );

  // Persist toggle state to localStorage
  const handleToggleDataMappings = useCallback((checked: boolean) => {
    setShowDataMappings(checked);
    if (!layoutKey) return;
    try {
      const existing = localStorage.getItem(layoutKey);
      const parsed = existing ? JSON.parse(existing) : {};
      parsed._showDataMappings = checked;
      localStorage.setItem(layoutKey, JSON.stringify(parsed));
    } catch { /* ignore */ }
  }, [layoutKey]);

  // Load saved positions from localStorage on mount
  useEffect(() => {
    if (!layoutKey) return;
    const saved = localStorage.getItem(layoutKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Extract positions (all keys except _showDataMappings)
        const positions: Record<string, { x: number; y: number }> = {};
        for (const [key, value] of Object.entries(parsed)) {
          if (key.startsWith('_')) continue;
          if (value && typeof value === 'object' && 'x' in (value as any) && 'y' in (value as any)) {
            positions[key] = value as { x: number; y: number };
          }
        }
        positionsRef.current = positions;
      } catch { /* ignore */ }
    }
  }, [layoutKey]);

  // When propNodes change (status updates from polling), merge with saved positions
  useEffect(() => {
    if (!propNodes.length) return;
    const merged = propNodes.map((node) => ({
      ...node,
      position: positionsRef.current[node.id] || node.position,
    }));
    setNodes(merged);

    if (!initializedRef.current) {
      initializedRef.current = true;
    }
  }, [propNodes, setNodes]);

  // Save positions to localStorage on drag stop
  const handleNodeDragStop = useCallback((_event: React.MouseEvent, node: Node) => {
    positionsRef.current[node.id] = node.position;
    if (!layoutKey) return;
    setNodes((currentNodes) => {
      try {
        const existing = localStorage.getItem(layoutKey);
        const parsed = existing ? JSON.parse(existing) : {};
        // Preserve metadata keys (prefixed with _), update positions
        currentNodes.forEach((n) => { parsed[n.id] = n.position; });
        localStorage.setItem(layoutKey, JSON.stringify(parsed));
      } catch { /* ignore */ }
      return currentNodes;
    });
  }, [layoutKey, setNodes]);

  // Auto-scroll to the first running node whenever nodes change
  useEffect(() => {
    if (!reactFlowInstance || !nodes.length) return;

    const runningNode = nodes.find((n) => n.data?.status === "running");
    if (runningNode) {
      const timer = setTimeout(() => {
        reactFlowInstance.setCenter(
          runningNode.position.x + 125,
          runningNode.position.y + 50,
          { zoom: 0.85, duration: 600 }
        );
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [nodes, reactFlowInstance]);

  return (
    <ReactFlow
      nodes={nodes}
      edges={displayedEdges}
      onNodesChange={onNodesChange}
      onNodeDragStop={handleNodeDragStop}
      nodeTypes={nodeTypes}
      defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
      fitView
      fitViewOptions={{
        padding: 0.3,
        minZoom: 0.3,
        maxZoom: 1.2,
      }}
      nodesDraggable={true}
      nodesConnectable={false}
      elementsSelectable={true}
      deleteKeyCode={null}
      style={{ width: "100%", height: "100%" }}
    >
      <Controls />
      <Background color="#f8f9fa" gap={16} />
      <Panel position="top-left">
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showDataMappings}
              onChange={(e) => handleToggleDataMappings(e.target.checked)}
            />
          }
          label={
            <Typography sx={{ fontSize: '11px', color: '#64748B', userSelect: 'none' }}>
              Show data mappings
            </Typography>
          }
          sx={{
            bgcolor: 'rgba(255,255,255,0.9)',
            borderRadius: '8px',
            px: 1,
            py: 0.25,
            border: '1px solid #e2e8f0',
            m: 0,
          }}
        />
      </Panel>
    </ReactFlow>
  );
};

const WorkflowRunDetailsContent: React.FC<WorkflowDetailsContentProps> = ({
  workflowRunDetails,
  activeTabIndex,
  onClose,
  onTabChange,
  nodes,
  edges,
  nodeTypes,
  layoutKey,
}) => {
  const a11yProps = (index: number) => {
    return { id: `tab-${index}`, "aria-controls": `tabpanel-${index}` };
  };

  return (
    <Box sx={{ display: "grid", gap: 2, mb: 2, p: 3 }}>
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs
          value={activeTabIndex}
          onChange={onTabChange}
          aria-label="run details tabs"
          variant="fullWidth"
        >
          <Tab
            sx={{ textTransform: "none" }}
            label={`Run Visualization`}
            {...a11yProps(0)}
          />
          <Tab
            sx={{ textTransform: "none" }}
            label="Workflow output"
            {...a11yProps(1)}
          />
        </Tabs>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <GeneralTabPanel value={activeTabIndex} index={1}>
          <Box
            sx={{
              px: 3,
              pb: 1,
              width: "95%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {workflowRunDetails.final_output == null ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontStyle: "italic" }}
              >
                {workflowRunDetails.status === "running" ? "Workflow is still running. Final output will be available once the workflow completes." : "No output available for this workflow yet."}
              </Typography>
            ) : (
              <Box
                sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word", m: 0 }}
                component="div"
                dangerouslySetInnerHTML={{
                  __html: (() => {
                    try {
                      return marked.parse(workflowRunDetails.final_output);
                    } catch (e) {
                      if (e instanceof Error) {
                        console.log("Error parsing workflow output:", e.message);
                      } else {
                        console.log("Error parsing workflow output:", e);
                      }
                      return "<i>Error parsing prompt content.</i>";
                    }
                  })(),
                }}
              />
            )}
          </Box>
        </GeneralTabPanel>

        <GeneralTabPanel
          value={activeTabIndex}
          index={0}
          sx={{ p: 3, height: "calc(100vh - 220px)" }}
        >
          <Paper
            sx={{
              height: "100%",
              width: "100%",
              border: "1px solid #e0e0e0",
              position: "relative",
            }}
            elevation={0}
          >
            <ReactFlowProvider>
              <RunVisualization
                nodes={nodes}
                edges={edges}
                nodeTypes={nodeTypes}
                layoutKey={layoutKey}
              />
            </ReactFlowProvider>
          </Paper>
        </GeneralTabPanel>
      </Box>

      <Divider sx={{ my: 1 }} />
    </Box>
  );
};

export default WorkflowRunDetailsContent;
